import type { LabelInValue } from 'models';
import { useTranslation } from 'react-i18next';
import { isEmail } from 'react-multi-email';
import { Form, FormActions, Modal, MultiValueInput, TextInput, message } from 'ui';
import { useInviteUsers } from '../../../queries/mutations/useInviteUsers';

type EmailInviteFormValues = {
  communityId: string;
  emails: string[];
};

type P = {
  community?: LabelInValue;
  isOpened: boolean;
  onCancel: () => void;
};

const EmailInviteModal = ({ community, isOpened, onCancel }: P) => {
  const { isPending: isInvitingUsers, mutate: inviteUsers } = useInviteUsers();
  const { t } = useTranslation();

  const onFinish = (data: EmailInviteFormValues) => {
    inviteUsers(
      {
        id: data.communityId,
        invitees: {
          emails: data.emails,
          phones: [],
          userIds: [],
        },
      },
      {
        onSuccess() {
          message.success('Invitations sent successfully');
          onCancel();
        },
      },
    );
  };

  const initialValues = {
    communityId: community?.value,
  };

  return (
    <Modal isOpened={isOpened} onCancel={onCancel} title="Invite via e-mail">
      <Modal.HugContent>
        <Form<EmailInviteFormValues> initialValues={initialValues} onFinish={onFinish}>
          <TextInput item={{ hidden: true, name: 'communityId', rules: [{ required: true }] }} />
          <Form.Item<EmailInviteFormValues>
            extra="Type email address and press enter, space or comma - Or paste from a spreadsheet!"
            label="Emails"
            name="emails"
            rules={[
              {
                // eslint-disable-next-line @typescript-eslint/require-await -- Antd interface need async validator
                async validator(_, value) {
                  if (Array.isArray(value)) {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                    const invalidNumbers = value.filter((email) => !isEmail(email));
                    if (invalidNumbers.length > 0) {
                      throw new Error(
                        t(
                          'Error: Some emails are invalid. Please enter a valid email address (e.g., example@email.com)',
                        ),
                      );
                    }
                  }
                },
              },
              {
                required: true,
              },
            ]}
          >
            <MultiValueInput allowInvalidValues />
          </Form.Item>

          <Modal.HugContent.Footer>
            <FormActions
              right={{
                children: 'Back',
                onClick() {
                  onCancel();
                },
              }}
              submit={{
                children: 'Send invitations',
                loading: isInvitingUsers,
              }}
            />
          </Modal.HugContent.Footer>
        </Form>
      </Modal.HugContent>
    </Modal>
  );
};

export default EmailInviteModal;
